import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { api } from '../services/api.js';
import { Distributor } from '../types/index.js';
import { Truck, Plus, Edit2, Trash2, X, Mail, Phone } from 'lucide-react';
import { ConfirmDialog } from '../components/ui/ConfirmDialog';

const Distributors: React.FC = () => {
  const [distributors, setDistributors] = useState<Distributor[]>([]);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [editingDistributor, setEditingDistributor] = useState<Distributor | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [distributorToDelete, setDistributorToDelete] = useState<{ id: string; name: string } | null>(null);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    contact_email: '',
    contact_phone: '',
    contact_person_name: '',
    contact_person_position: '',
    contact_person_phone: '',
    contact_person_email: '',
    address: '',
    website: '',
    tax_id: '',
    minimum_order_value: '',
    payment_terms: '',
    delivery_days: [] as string[],
    lead_time_days: '',
    notes: '',
  });

  useEffect(() => {
    fetchDistributors();
  }, []);

  const fetchDistributors = async () => {
    try {
      const res = await api.get('/distributors');
      setDistributors(res.data);
    } catch (err) {
      console.error('Failed to fetch distributors', err);
      toast.error('Error loading distributors');
    }
  };

  const handleCreate = async () => {
    if (!formData.name) {
      toast.warning('Please enter a name');
      return;
    }

    setLoading(true);
    try {
      await api.post('/distributors', formData);
      toast.success('Distributor created successfully!');
      closeForm();
      fetchDistributors();
    } catch (err) {
      console.error('Failed to create distributor', err);
      toast.error('Error creating distributor');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async () => {
    if (!editingDistributor) return;

    setLoading(true);
    try {
      await api.put(`/distributors/${editingDistributor.id}`, formData);
      toast.success('Distributor updated successfully!');
      closeForm();
      fetchDistributors();
    } catch (err) {
      console.error('Failed to update distributor', err);
      toast.error('Error updating distributor');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!distributorToDelete) return;
    
    try {
      await api.delete(`/distributors/${distributorToDelete.id}`);
      toast.success('Distributor deleted');
      fetchDistributors();
    } catch (err) {
      console.error('Failed to delete distributor', err);
      toast.error('Error deleting. There might be linked POS stores.');
    }
  };

  const openDeleteDialog = (id: string, name: string) => {
    setDistributorToDelete({ id, name });
    setDeleteDialogOpen(true);
  };

  const openFormForEdit = (distributor: Distributor) => {
    setEditingDistributor(distributor);
    setFormData({
      name: distributor.name,
      contact_email: distributor.contact_email || '',
      contact_phone: distributor.contact_phone || '',
      contact_person_name: distributor.contact_person_name || '',
      contact_person_position: distributor.contact_person_position || '',
      contact_person_phone: distributor.contact_person_phone || '',
      contact_person_email: distributor.contact_person_email || '',
      address: distributor.address || '',
      website: distributor.website || '',
      tax_id: distributor.tax_id || '',
      minimum_order_value: distributor.minimum_order_value?.toString() || '',
      payment_terms: distributor.payment_terms || '',
      delivery_days: distributor.delivery_days || [],
      lead_time_days: distributor.lead_time_days?.toString() || '',
      notes: distributor.notes || '',
    });
    setIsFormOpen(true);
  };

  const openFormForCreate = () => {
    setEditingDistributor(null);
    setFormData({
      name: '',
      contact_email: '',
      contact_phone: '',
      contact_person_name: '',
      contact_person_position: '',
      contact_person_phone: '',
      contact_person_email: '',
      address: '',
      website: '',
      tax_id: '',
      minimum_order_value: '',
      payment_terms: '',
      delivery_days: [],
      lead_time_days: '',
      notes: '',
    });
    setIsFormOpen(true);
  };

  const closeForm = () => {
    setIsFormOpen(false);
    setEditingDistributor(null);
    setFormData({
      name: '',
      contact_email: '',
      contact_phone: '',
      contact_person_name: '',
      contact_person_position: '',
      contact_person_phone: '',
      contact_person_email: '',
      address: '',
      website: '',
      tax_id: '',
      minimum_order_value: '',
      payment_terms: '',
      delivery_days: [],
      lead_time_days: '',
      notes: '',
    });
  };

  return (
    <div className="max-w-6xl mx-auto p-4 space-y-4 md:space-y-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 md:gap-0">
        <div className="flex items-center gap-3">
          <div className="p-2.5 md:p-3 bg-blue-100 rounded-xl flex-shrink-0">
            <Truck size={24} className="text-blue-600 md:w-7 md:h-7" />
          </div>
          <div>
            <h1 className="text-2xl md:text-3xl font-black text-gray-900">Distributors</h1>
            <p className="text-gray-600 text-xs md:text-sm">Manage your beverage distributors</p>
          </div>
        </div>
        
        <button
          onClick={openFormForCreate}
          className="flex items-center justify-center gap-2 px-5 py-3 bg-blue-500 text-white rounded-xl font-bold hover:bg-blue-600 transition-colors shadow-md hover:shadow-lg active:scale-95"
        >
          <Plus size={18} />
          <span>New Distributor</span>
        </button>
      </div>

      {/* Desktop Table View */}
      {distributors.length === 0 ? (
        <div className="text-center py-12 md:py-16 bg-gray-50 rounded-2xl border-2 border-dashed border-gray-300">
          <Truck size={48} className="mx-auto mb-3 md:mb-4 text-gray-400 md:w-16 md:h-16" />
          <p className="text-lg md:text-xl font-bold text-gray-600 mb-2">No distributors available</p>
          <p className="text-sm text-gray-500 mb-4">Create your first distributor</p>
          <button
            onClick={openFormForCreate}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-500 text-white rounded-xl font-bold hover:bg-blue-600 transition-colors"
          >
            <Plus size={18} />
            <span>Add Distributor</span>
          </button>
        </div>
      ) : (
        <>
          {/* Desktop Table */}
          <div className="hidden md:block bg-white rounded-2xl shadow-sm overflow-hidden border border-gray-100">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">Name</th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">Contact Email</th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">Phone</th>
                  <th className="px-6 py-4 text-right text-xs font-bold text-gray-600 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {distributors.map(dist => (
                  <tr key={dist.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4">
                      <p className="font-bold text-gray-900">{dist.name}</p>
                      {dist.notes && (
                        <p className="text-xs text-gray-500 mt-1 line-clamp-1">{dist.notes}</p>
                      )}
                    </td>
                    <td className="px-6 py-4 text-gray-700 text-sm">
                      {dist.contact_email || <span className="text-gray-400 italic">–</span>}
                    </td>
                    <td className="px-6 py-4 text-gray-700 text-sm">
                      {dist.contact_phone || <span className="text-gray-400 italic">–</span>}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => openFormForEdit(dist)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          title="Edit"
                        >
                          <Edit2 size={18} />
                        </button>
                        <button
                          onClick={() => openDeleteDialog(dist.id, dist.name)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Delete"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile Card View */}
          <div className="md:hidden space-y-3">
            {distributors.map(dist => (
              <div key={dist.id} className="bg-white rounded-2xl border-2 border-gray-100 shadow-sm p-4">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <h3 className="font-black text-base text-gray-900">{dist.name}</h3>
                    {dist.notes && (
                      <p className="text-xs text-gray-500 mt-1 line-clamp-2">{dist.notes}</p>
                    )}
                  </div>
                </div>
                <div className="space-y-2 mb-3 text-sm">
                  {dist.contact_email && (
                    <div className="flex items-center gap-2 text-gray-700">
                      <Mail size={14} className="text-gray-400 flex-shrink-0" />
                      <span className="truncate">{dist.contact_email}</span>
                    </div>
                  )}
                  {dist.contact_phone && (
                    <div className="flex items-center gap-2 text-gray-700">
                      <Phone size={14} className="text-gray-400 flex-shrink-0" />
                      <span>{dist.contact_phone}</span>
                    </div>
                  )}
                  {!dist.contact_email && !dist.contact_phone && (
                    <p className="text-xs text-gray-400 italic">No contact info</p>
                  )}
                </div>
                <div className="flex gap-2 pt-3 border-t border-gray-100">
                  <button
                    onClick={() => openFormForEdit(dist)}
                    className="flex-1 flex items-center justify-center gap-2 p-2.5 bg-blue-50 text-blue-600 rounded-xl font-bold text-sm hover:bg-blue-100 transition-colors"
                  >
                    <Edit2 size={16} />
                    <span>Edit</span>
                  </button>
                  <button
                    onClick={() => openDeleteDialog(dist.id, dist.name)}
                    className="p-2.5 bg-red-50 text-red-600 rounded-xl hover:bg-red-100 transition-colors"
                    title="Delete"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Create/Edit Modal */}
      {isFormOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end md:items-center justify-center z-50 p-0 md:p-4">
          <div className="bg-white rounded-t-[2rem] md:rounded-2xl shadow-2xl max-w-2xl w-full max-h-[92vh] md:max-h-[85vh] flex flex-col">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-4 md:px-6 py-3.5 md:py-4 flex items-center justify-between rounded-t-[2rem] md:rounded-t-2xl flex-shrink-0">
              <h2 className="text-xl md:text-2xl font-black text-gray-900">
                {editingDistributor ? 'Edit Distributor' : 'New Distributor'}
              </h2>
              <button
                onClick={closeForm}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            <div className="p-4 md:p-6 space-y-5 md:space-y-6 overflow-y-auto flex-1">
              {/* Company Information */}
              <div className="space-y-3 md:space-y-4">
                <h3 className="text-base md:text-lg font-bold text-gray-900 pb-2 border-b border-gray-200">Company Information</h3>
                
                <div>
                  <label className="block text-xs md:text-sm font-semibold text-gray-700 mb-1.5">
                    Company Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-3 md:px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none text-base"
                    placeholder="e.g. Edeka Central"
                  />
                </div>

                <div>
                  <label className="block text-xs md:text-sm font-semibold text-gray-700 mb-1.5">
                    Address
                  </label>
                  <textarea
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    rows={2}
                    className="w-full px-3 md:px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none resize-none text-base"
                    placeholder="Street, City, Postal Code"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
                  <div>
                    <label className="block text-xs md:text-sm font-semibold text-gray-700 mb-1.5">
                      Website
                    </label>
                    <input
                      type="url"
                      value={formData.website}
                      onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                      className="w-full px-3 md:px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none text-base"
                      placeholder="https://example.com"
                    />
                  </div>

                  <div>
                    <label className="block text-xs md:text-sm font-semibold text-gray-700 mb-1.5">
                      Tax ID / VAT
                    </label>
                    <input
                      type="text"
                      value={formData.tax_id}
                      onChange={(e) => setFormData({ ...formData, tax_id: e.target.value })}
                      className="w-full px-3 md:px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none text-base"
                      placeholder="DE123456789"
                    />
                  </div>
                </div>
              </div>

              {/* Primary Contact */}
              <div className="space-y-3 md:space-y-4">
                <h3 className="text-base md:text-lg font-bold text-gray-900 pb-2 border-b border-gray-200">Primary Contact</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                      Email
                    </label>
                    <input
                      type="email"
                      value={formData.contact_email}
                      onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                      placeholder="orders@example.com"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                      Phone
                    </label>
                    <input
                      type="tel"
                      value={formData.contact_phone}
                      onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                      placeholder="+49 123 456789"
                    />
                  </div>
                </div>
              </div>

              {/* Contact Person */}
              <div className="space-y-3 md:space-y-4">
                <h3 className="text-base md:text-lg font-bold text-gray-900 pb-2 border-b border-gray-200">Contact Person</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                      Name
                    </label>
                    <input
                      type="text"
                      value={formData.contact_person_name}
                      onChange={(e) => setFormData({ ...formData, contact_person_name: e.target.value })}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                      placeholder="Max Mustermann"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                      Position
                    </label>
                    <input
                      type="text"
                      value={formData.contact_person_position}
                      onChange={(e) => setFormData({ ...formData, contact_person_position: e.target.value })}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                      placeholder="Sales Manager"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                      Phone
                    </label>
                    <input
                      type="tel"
                      value={formData.contact_person_phone}
                      onChange={(e) => setFormData({ ...formData, contact_person_phone: e.target.value })}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                      placeholder="+49 170 1234567"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                      Email
                    </label>
                    <input
                      type="email"
                      value={formData.contact_person_email}
                      onChange={(e) => setFormData({ ...formData, contact_person_email: e.target.value })}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                      placeholder="max@example.com"
                    />
                  </div>
                </div>
              </div>

              {/* Order Terms */}
              <div className="space-y-3 md:space-y-4">
                <h3 className="text-base md:text-lg font-bold text-gray-900 pb-2 border-b border-gray-200">Order Terms</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                      Minimum Order Value (€)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.minimum_order_value}
                      onChange={(e) => setFormData({ ...formData, minimum_order_value: e.target.value })}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                      placeholder="250.00"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                      Payment Terms
                    </label>
                    <input
                      type="text"
                      value={formData.payment_terms}
                      onChange={(e) => setFormData({ ...formData, payment_terms: e.target.value })}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                      placeholder="Net 30, Net 60"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                    Lead Time (days)
                  </label>
                  <input
                    type="number"
                    value={formData.lead_time_days}
                    onChange={(e) => setFormData({ ...formData, lead_time_days: e.target.value })}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                    placeholder="2"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Delivery Days
                  </label>
                  <div className="grid grid-cols-4 gap-2">
                    {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map(day => (
                      <label key={day} className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.delivery_days.includes(day)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setFormData({ ...formData, delivery_days: [...formData.delivery_days, day] });
                            } else {
                              setFormData({ ...formData, delivery_days: formData.delivery_days.filter(d => d !== day) });
                            }
                          }}
                          className="w-4 h-4 text-blue-600"
                        />
                        <span className="text-sm">{day.substring(0, 3)}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                  Notes
                </label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows={3}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none resize-none"
                  placeholder="Additional information..."
                />
              </div>
            </div>

            {/* Action Buttons */}
            <div className="px-4 md:px-6 py-3.5 md:py-4 bg-gray-50 border-t border-gray-200 flex gap-2 md:gap-3 flex-shrink-0">
              <button
                type="button"
                onClick={closeForm}
                className="flex-1 px-4 py-3 bg-gray-200 text-gray-700 rounded-xl font-bold hover:bg-gray-300 active:scale-95 transition-all text-sm md:text-base"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={editingDistributor ? handleUpdate : handleCreate}
                disabled={loading}
                className="flex-1 px-4 py-3 bg-blue-500 text-white rounded-xl font-bold hover:bg-blue-600 active:scale-95 transition-all disabled:opacity-50 text-sm md:text-base"
              >
                {loading ? 'Saving...' : (editingDistributor ? 'Update' : 'Create')}
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title="Delete Distributor?"
        message={`Are you sure you want to delete "${distributorToDelete?.name}"? This action cannot be undone.`}
        confirmText="Delete Distributor"
        variant="danger"
        onConfirm={handleDelete}
      />
    </div>
  );
};

export default Distributors;

